import { useQuery } from "@tanstack/react-query";

import { allQueryKeys } from "#app/_api/allQueryKeys";
import type { GetCheckData } from "#app/_api/external-fund-transfer-service/get-check";
import type { GetChecksData } from "#app/_api/external-fund-transfer-service/get-checks";

type CheckAccount = GetCheckData["bairdAccountInfo"][number]["accountNumber"];
type CheckAmount = GetCheckData["bairdAccountInfo"][number]["amount"];

export interface CheckVm extends Omit<GetCheckData, "bairdAccountInfo"> {
  account: CheckAccount;
  accounts: CheckAccount[];
  amount: CheckAmount;
  amounts: CheckAmount[];
  split: boolean;
}

function toCheckVm(value: GetCheckData): CheckVm {
  const { bairdAccountInfo, ...check } = value;

  const split = bairdAccountInfo.length > 1;

  const accounts = bairdAccountInfo.map((el) => el.accountNumber);
  const account = accounts[0] ?? "";

  const amounts = bairdAccountInfo.map((el) => el.amount);
  const amount = split
    ? amounts.some((el) => el === undefined)
      ? 0
      : amounts.reduce((acc, cur) => acc + cur, 0)
    : amounts[0]!;

  return {
    ...check,
    account,
    accounts,
    amount,
    amounts,
    split,
  } satisfies CheckVm;
}

function checksToVm(values: GetCheckData[] | undefined): CheckVm[] {
  return values?.map((el) => toCheckVm(el)) ?? [];
}

export function selectChecks(values: GetChecksData) {
  return checksToVm(values.data) ?? ([] as CheckVm[]);
}

function selectCheck(value: GetCheckData) {
  return toCheckVm(value);
}

export function useChecksQuery({ enabled }: { enabled?: boolean } = {}) {
  return useQuery({
    ...allQueryKeys.externalFundTransferService.checks({ AllResults: true }),
    select: selectChecks,
    // Prevents checks dialog from unmounting unexpectedly
    refetchOnWindowFocus: false,
    enabled,
  });
}

export function useCheckQuery(compositeId: string) {
  return useQuery({
    ...allQueryKeys.externalFundTransferService.check({ compositeId }),
    select: selectCheck,
  });
}
